import { Frame } from '../atoms/Frame';
import { NumList } from '../atoms/NumList';
import { BulletBad } from '../atoms/Bullets';
import { CheckRow } from '../atoms/CheckRow';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { Cross } from '../atoms/Icons';
import { useT } from '../../../i18n/useT';
import { P, S, T } from '../../../styles/tokens';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun, ArtefactResult } from '../../../api/flow-types';

const ERROR_SOFT_BG = '#FDE8E8';

/** BUG-1230: resolve display label for an artefact role from checkpoint requirements. */
function labelForRole(checkpoint: Checkpoint, role: string): string {
  const req = checkpoint.artefact_requirements.find((r) => r.role === role);
  return req?.label ?? role;
}

/** BUG-1230: map ArtefactResult.status to CheckRow status prop. */
function mapStatus(status: ArtefactResult['status']): 'done' | 'fail' | 'pending' {
  if (status === 'passed') return 'done';
  if (status === 'failed') return 'fail';
  return 'pending';
}

interface TemplateProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  onCta: () => void;
}

export function RejectScreen({ checkpoint, attempt, onCta }: TemplateProps) {
  const retryLabel = useT('flow.cta.retry');
  // attempt must be non-null when this template renders (rejected state)
  const att = attempt!;
  const counter = useT('flow.attempt.x_of_y', {
    current: att.attempt_number,
    total: checkpoint.max_attempts,
  });

  const hint = checkpoint.rejection_hint;

  // BUG-1230: show per-artefact matrix only for rejected attempts with attribution data
  const artefactResults =
    att.verdict === 'rejected' && att.artefact_results && att.artefact_results.length > 0
      ? att.artefact_results
      : null;

  return (
    <Frame>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* Rejection banner — matches prototype TF_Step2Rejected error block */}
        <div
          style={{
            marginTop: 14,
            padding: 14,
            background: ERROR_SOFT_BG,
            border: '1px solid rgba(209,67,67,0.30)',
            borderRadius: 14,
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                background: P.error,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: '#fff',
              }}
            >
              <Cross size={14} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ font: T.headline, color: P.error, fontWeight: 700 }}>
                Скрин не подошёл
              </div>
              <div
                style={{
                  font: T.footnote,
                  color: S.label,
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                Проверь требования ниже и попробуй ещё раз.
              </div>
              <div
                style={{
                  font: T.caption,
                  color: S.secondaryLabel,
                  marginTop: 8,
                }}
              >
                {counter}
              </div>
            </div>
          </div>
        </div>

        {/* BUG-1230: per-artefact result matrix (only when attribution data present) */}
        {artefactResults && (
          <div
            style={{
              background: P.card,
              borderRadius: 16,
              padding: '14px 14px',
              border: `1px solid ${P.line}`,
            }}
          >
            <div
              style={{
                font: T.caption,
                fontWeight: 700,
                color: S.label,
                marginBottom: 10,
              }}
            >
              Результат проверки:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {artefactResults.map((ar) => (
                <div key={ar.role}>
                  <CheckRow
                    text={labelForRole(checkpoint, ar.role)}
                    status={mapStatus(ar.status)}
                  />
                  {ar.status === 'failed' && ar.reasons.length > 0 && (
                    <div
                      style={{
                        paddingLeft: 14,
                        marginTop: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                      }}
                    >
                      {ar.reasons.map((reason, i) => (
                        <BulletBad key={`${ar.role}-reason-${i}`} text={reason} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reject reasons list */}
        {att.reject_reasons.length > 0 && (
          <div
            style={{
              background: P.card,
              borderRadius: 16,
              padding: '14px 14px',
              border: `1px solid ${P.line}`,
            }}
          >
            <div
              style={{
                font: T.caption,
                fontWeight: 700,
                color: S.label,
                marginBottom: 10,
              }}
            >
              Причины отклонения:
            </div>
            <NumList items={att.reject_reasons} />
          </div>
        )}

        {/* FTR-1229: Structured rejection hint block (requirements, mistakes, tip) */}
        {hint && hint.title && (
          <div
            style={{
              background: P.card,
              borderRadius: 16,
              padding: 14,
              border: `1px solid ${P.line}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {/* Title */}
            <div style={{ font: T.caption, fontWeight: 700, color: S.label }}>
              {hint.title}
            </div>

            {/* Requirements list */}
            {hint.requirements.length > 0 && (
              <NumList items={hint.requirements} />
            )}

            {/* Common mistakes */}
            {hint.common_mistakes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div
                  style={{
                    font: T.caption,
                    fontWeight: 700,
                    color: P.error,
                    marginTop: 4,
                  }}
                >
                  Частые ошибки:
                </div>
                {hint.common_mistakes.map((text, i) => (
                  <BulletBad key={`mistake-${i}`} text={text} />
                ))}
              </div>
            )}

            {/* Where to look tip */}
            {hint.where_to_look && (
              <div
                style={{
                  font: T.footnote,
                  color: S.secondaryLabel,
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                {hint.where_to_look}
              </div>
            )}
          </div>
        )}
      </div>
      <WizardCtaBar label={retryLabel} onClick={onCta} />
    </Frame>
  );
}
